import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Plus, Trash2 } from 'lucide-react';
import * as dpService from 'features/dp/services/dpService';

const inputClass =
  'w-full px-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)] focus:outline-none focus:ring-2 focus:ring-teal-500';

function formatarData(iso) {
  if (!iso) return '—';
  const [a, m, d] = String(iso).substring(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a}` : iso;
}

export default function BancoHorasTab({ podeEditar }) {
  const [registros, setRegistros] = useState([]);
  const [saldos, setSaldos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [abaInterna, setAbaInterna] = useState('lancamentos');
  const [funcionarios, setFuncionarios] = useState([]);

  const [form, setForm] = useState({
    funcionario_id: '', data: '', tipo: 'extra', horas: '', observacao: '',
  });

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [res, saldoRes] = await Promise.all([
        dpService.listarBancoHoras({ per_page: 100 }),
        dpService.saldoGeral(),
      ]);
      setRegistros(res.data?.data?.data ?? res.data?.data ?? []);
      setSaldos(saldoRes.data?.data ?? []);
    } catch {
      setErro('Erro ao carregar dados');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    dpService.listarFuncionarios({ status: 'ativo', per_page: 200 })
      .then(res => setFuncionarios(res.data?.data?.data ?? res.data?.data ?? []))
      .catch(() => {});
  }, []);

  const setField = (f) => (e) => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErro(null);
    try {
      await dpService.criarBancoHoras(form);
      setMostrarForm(false);
      setForm({ funcionario_id: '', data: '', tipo: 'extra', horas: '', observacao: '' });
      await carregar();
    } catch (err) {
      setErro(err?.response?.data?.message ?? 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este lançamento?')) return;
    try {
      await dpService.excluirBancoHoras(id);
      await carregar();
    } catch (err) {
      setErro(err?.response?.data?.message ?? 'Erro ao excluir');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Clock size={20} className="text-teal-600" />
          <h2 className="text-base font-bold text-[color:var(--text)]">Cartão de Ponto</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-xl border border-[color:var(--border)] overflow-hidden">
            <button onClick={() => setAbaInterna('lancamentos')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${abaInterna === 'lancamentos' ? 'bg-teal-600 text-white' : 'text-[color:var(--text-muted)] hover:bg-[color:var(--surface-muted)]'}`}>
              Lançamentos
            </button>
            <button onClick={() => setAbaInterna('saldos')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${abaInterna === 'saldos' ? 'bg-teal-600 text-white' : 'text-[color:var(--text-muted)] hover:bg-[color:var(--surface-muted)]'}`}>
              Saldos
            </button>
          </div>
          {podeEditar && abaInterna === 'lancamentos' && (
            <button onClick={() => setMostrarForm(v => !v)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700 transition-colors">
              <Plus size={14} /> Lançar
            </button>
          )}
        </div>
      </div>

      {erro && <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-xl">{erro}</div>}

      {mostrarForm && (
        <form onSubmit={handleSubmit} className="border border-[color:var(--border)] rounded-2xl p-5 space-y-4 bg-[color:var(--surface-muted)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Funcionário *</label><select required value={form.funcionario_id} onChange={setField('funcionario_id')} className={inputClass}><option value="">Selecione</option>{funcionarios.map(f => <option key={f.id} value={f.id}>{f.pessoa?.nome}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Data *</label><input type="date" required value={form.data} onChange={setField('data')} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Tipo</label><select value={form.tipo} onChange={setField('tipo')} className={inputClass}><option value="extra">Hora Extra</option><option value="falta">Falta</option><option value="atraso">Atraso</option><option value="compensacao">Compensação</option></select></div>
            <div><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Horas *</label><input type="number" step="0.01" required value={form.horas} onChange={setField('horas')} placeholder="Ex: 2.5" className={inputClass} /></div>
            <div className="sm:col-span-2"><label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">Observação</label><input value={form.observacao} onChange={setField('observacao')} className={inputClass} /></div>
          </div>
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={() => setMostrarForm(false)} className="px-4 py-2 text-sm rounded-xl border border-[color:var(--border)] text-[color:var(--text-muted)]">Cancelar</button>
            <button type="submit" disabled={salvando} className="px-4 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60">{salvando ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </form>
      )}

      {carregando ? (
        <div className="flex justify-center py-12"><div className="animate-spin w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full" /></div>
      ) : abaInterna === 'saldos' ? (
        <div className="overflow-x-auto rounded-xl border border-[color:var(--border)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--surface-muted)]">
              <tr>
                {['Funcionário', 'Extras', 'Faltas', 'Saldo'].map(col => (
                  <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-[color:var(--text-muted)]">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--border)]">
              {(Array.isArray(saldos) ? saldos : []).map((s, i) => (
                <tr key={i} className="hover:bg-[color:var(--surface-muted)] transition-colors">
                  <td className="px-4 py-3 font-medium text-[color:var(--text)]">{s.nome ?? s.funcionario?.pessoa?.nome}</td>
                  <td className="px-4 py-3 text-green-600">{Number(s.extras || 0).toFixed(1)}h</td>
                  <td className="px-4 py-3 text-red-500">{Number(s.faltas || 0).toFixed(1)}h</td>
                  <td className="px-4 py-3 font-semibold">
                    <span className={Number(s.saldo || 0) >= 0 ? 'text-green-600' : 'text-red-500'}>
                      {Number(s.saldo || 0).toFixed(1)}h
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : registros.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Clock size={40} className="text-[color:var(--text-muted)] opacity-30 mb-3" />
          <p className="text-sm text-[color:var(--text-muted)]">Nenhum lançamento encontrado.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[color:var(--border)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--surface-muted)]">
              <tr>
                {['Funcionário', 'Data', 'Tipo', 'Horas', 'Observação', 'Ações'].map(col => (
                  <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-[color:var(--text-muted)] whitespace-nowrap">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--border)]">
              {registros.map(r => (
                <tr key={r.id} className="hover:bg-[color:var(--surface-muted)] transition-colors">
                  <td className="px-4 py-3 font-medium text-[color:var(--text)]">{r.funcionario?.pessoa?.nome ?? '—'}</td>
                  <td className="px-4 py-3 text-[color:var(--text-muted)]">{formatarData(r.data)}</td>
                  <td className="px-4 py-3 capitalize text-[color:var(--text-muted)]">{(r.tipo || '').replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3 font-medium">
                    <span className={r.tipo === 'falta' || r.tipo === 'atraso' ? 'text-red-500' : 'text-green-600'}>
                      {Number(r.horas || 0).toFixed(1)}h
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[color:var(--text-muted)] truncate max-w-[200px]">{r.observacao || '—'}</td>
                  <td className="px-4 py-3">
                    {podeEditar && (
                      <button onClick={() => excluir(r.id)} title="Excluir"
                        className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
